import {
    AfterViewChecked,
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    Output,
    ViewChild,
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { SearchFormType } from '../search-page-type.enum';
import { SearchPageService } from '../search-page.service';
import { FromToLabel, SearchParam, SearchParams, SearchParamType, SearchTypeInfo } from '../search-param.model';
import { filterListToChip } from '../helpers';
import { IAppliedFilterChip } from './types';
import { UserConfigurationSettingsService } from '../../services/settings/user-configuration-settings.service';

@Component({
    selector: 'ap-search-filter',
    templateUrl: './ap-search-filter.component.html',
    styleUrls: ['./ap-search-filter.component.scss'],
})
export class ApSearchFilterComponent implements AfterViewInit, AfterViewChecked, OnDestroy {
    static readonly SearchParamType = SearchParamType;
    readonly SearchParamType = ApSearchFilterComponent.SearchParamType;
    static readonly SearchFormType = SearchFormType;
    readonly SearchFormType = SearchFormType;

    @ViewChild('row')
    rowElement?: ElementRef<HTMLElement>;

    appliedFilterChips: IAppliedFilterChip[] = [];
    searchParamsMapByName: Record<string, SearchParam> = {};

    secondaryFiltersOpen: boolean = false;
    _searchParams: SearchParams = new SearchParams([]);
    paramsGroupedByRow: SearchParam[][] = [];

    rowSize$: BehaviorSubject<number> = new BehaviorSubject(0);

    @Input() set searchParams(params: SearchParams) {
        this._searchParams = params;
        this.paramsGroupedByRow = params.searchParamsFullGroupedByRow;
        this.searchParamsMapByName = Object.fromEntries(
            this.paramsGroupedByRow.flat(2).map((p) => [p.property.name, p]),
        );
        this.setFilterChips();
    }

    get searchParams(): SearchParams {
        return this._searchParams;
    }

    @Input()
    formType!: SearchFormType;
    @Input() isMultiSearchEnabled = true;
    @Input() disableSearch: boolean = false;
    @Input() actualSearchType!: SearchTypeInfo;
    @Input() hideFilters: boolean = false;
    @Input() isBusy: boolean = false;
    @Input() searchTypes: SearchTypeInfo[] = [];
    @Input()
    searchOnInit: boolean = false;

    @Output() resetAllFilters = new EventEmitter<void>();
    @Output() removeFilter = new EventEmitter<
        string | FromToLabel
    >(); /*potrebbe bastare filter changed che viene scatenato ad ogni modifica del filtro anche quando viene resettato*/
    @Output() applyAllFilters = new EventEmitter<void>();
    @Output() filterChanged: EventEmitter<any> = new EventEmitter<any>();
    @Output() searchTypeChanged: EventEmitter<SearchTypeInfo> = new EventEmitter<SearchTypeInfo>();
    @Output()
    searchRequired: EventEmitter<any> = new EventEmitter<any>();

    @HostListener('window:resize', ['$event'])
    onResize(_event: Event) {
        this.setRowSize();
    }

    constructor(
        private searchPageService: SearchPageService,
        private userConfigurationSettingsService: UserConfigurationSettingsService,
    ) {}

    ngAfterViewInit() {
        this.searchPageService.loadFormCollections(this.formType);
        if (this.searchOnInit) {
            this.onSearch();
        }
    }
    ngAfterViewChecked() {
        this.setRowSize();
    }
    ngOnDestroy(): void {
        this.rowSize$.complete();
    }

    setRowSize = () => {
        const width = this.rowElement?.nativeElement.getBoundingClientRect().width;
        if (!!width && this.rowSize$.value !== width) this.rowSize$.next(width);
    };

    setFilterChips() {
        this.appliedFilterChips = [...filterListToChip(Object.values(this.paramsGroupedByRow.slice(1)).flat())];
    }

    isCurrentUserAuthorized = () =>
        this.userConfigurationSettingsService.isAuthorized(['configurationForSite_Set'], 'single');

    removeFilterChip(filterApplied: IAppliedFilterChip) {
        if (this.secondaryFiltersOpen) return;
        const { filterName } = filterApplied;
        this.resetParamToDefault(filterName);
        this.setFilterChips();
        this.removeFilter.emit(filterName);
    }

    handleSearchAll() {
        this.searchRequired.emit();
    }

    setParamValue = (paramName: string, value: any) => {
        this._searchParams.params[paramName] = value;
    };

    resetParamToDefault = (filterName: string) => {
        const searchParam: SearchParam = this.searchParamsMapByName[filterName];
        if (!searchParam) return; //throw error??
        if (searchParam.property.type === SearchParamType.fromTo) {
            searchParam.property.value = {};
        } else {
            searchParam.property.value = searchParam.property.defaultValue;
        }
        this.setParamValue(filterName, searchParam.property.value);
    };

    onSearch() {
        this.searchRequired.emit();
    }

    handleResetAll() {
        this.searchParams.resetSearchParamToDefault();
        this.resetAllFilters.emit();
    }

    onFilterChange(filterName: string, col: number, row: number) {
        this.setParamValue(filterName, this.searchParamsMapByName[filterName].property.value);
        this.filterChanged.emit({ filterName, col, row });
        this.setFilterChips();
    }

    onResetComboSelectedValue(filterName: string, col: number, row: number) {
        this.resetParamToDefault(filterName);
        this.filterChanged.emit({ filterName, col, row });
        this.setFilterChips();
    }

    onChangeSearchType = (searchType: SearchTypeInfo) => {
        this.searchTypeChanged.emit(searchType);
    };

    onChangeFiltersConfiguration = (filters: SearchParams) => (this.searchParams = filters);
}
